import React, { useState, useEffect } from "react";
import { Card, CardBody, CardTitle, Button, Spinner } from "reactstrap";
import Dropzone from "react-dropzone";
import { toast } from "react-toastify";
import { useBase64Converter } from "hooks/useBase64Converter";

interface FileWithPreview {
  id: number;
  url: string;
  name?: string;
}

const ProductImageInput = ({
  setFieldValue,
  initialImages = [],
}: {
  setFieldValue: any;
  initialImages?: FileWithPreview[];
}) => {
  const [selectedFiles, setSelectedFiles] =
    useState<FileWithPreview[]>(initialImages);
  const [productImages, setProductImages] = useState<File[]>([]);
  const { convertFilesToBase64 } = useBase64Converter();
  const MAX_IMAGE_SIZE = 5 * 1024 * 1024; // 5 MB
  const [loading, setLoading] = useState<boolean>(true);
  const [imagesFetched, setImagesFetched] = useState<boolean>(false);

  const handleAcceptedFiles = (acceptedFiles: File[]) => {
    if (selectedFiles.length >= 3) {
      toast.error("Maximum number of images reached");
      return;
    }
    const uniqueFiles = acceptedFiles.filter((file) => {
      if (file.size > MAX_IMAGE_SIZE) {
        toast.error("File size exceeds the maximum limit of 5 MB");
        return false; // Exclude the file if it exceeds the size limit
      }
      if (
        selectedFiles.some((selectedFile) => selectedFile.name === file.name)
      ) {
        toast.error("This image has already been added");
      }
      return !selectedFiles.some(
        (selectedFile) => selectedFile.name === file.name
      );
    });

    const filesWithPreview = uniqueFiles.map((file, index) => ({
      id: selectedFiles.length + index + 1,
      url: URL.createObjectURL(file),
      name: file.name,
    }));

    const updatedFiles = [...selectedFiles, ...filesWithPreview];

    const newFiles = uniqueFiles.map((file) => file);

    const files = [...newFiles, ...productImages];

    setProductImages(files.slice(0, 3));

    setSelectedFiles(updatedFiles.slice(0, 3));

    const base64Files = convertFilesToBase64(files);
    // Update Formik images with the new format
    setFieldValue("files", base64Files);
  };

  const handleDelete = (index: number) => {
    const newImages = selectedFiles.filter(
      (_, i) => i !== index
    ) as FileWithPreview[];

    const imagesNotInNewImages = initialImages?.filter(
      (initialImage) =>
        !newImages.some((newImage) => newImage.url === initialImage.url)
    );

    const imagesToDeleteIds = imagesNotInNewImages.map((image) => image.id);
    setFieldValue("images_to_delete", imagesToDeleteIds);

    const newFiles = productImages.filter((_, i) => i !== index);
    setSelectedFiles(newImages);
    setProductImages(newFiles);

    const base64Files = convertFilesToBase64(productImages);

    setFieldValue("files", base64Files);
  };

  const fetchImages = async () => {
    setLoading(true);
    // Simulate a delay for fetching images
    await new Promise((resolve) => setTimeout(resolve, 2000));
    setImagesFetched(true); // Set imagesFetched to true after fetching
    setLoading(false);
  };

  useEffect(() => {
    fetchImages();
  }, []);

  return (
    <Card>
      <CardBody>
        <CardTitle className="mb-3">Product Images</CardTitle>
        {loading ? (
          <div className="text-center">
            <Spinner color="primary" />
            <p>Loading images...</p>
          </div>
        ) : !imagesFetched ? (
          <div className="text-center">
            <p>No images found.</p>
          </div>
        ) : (
          <>
            {selectedFiles.length < 3 && (
              <Dropzone
                onDrop={handleAcceptedFiles}
                accept={{ "image/*": [] }} // Accept only images
                multiple={false}
                maxFiles={3}
              >
                {({ getRootProps, getInputProps }) => (
                  <div className="dropzone" {...getRootProps()}>
                    <input {...getInputProps()} />
                    <div className="dz-message needsclick">
                      <div className="mb-3">
                        <i className="display-4 text-muted bx bxs-cloud-upload" />
                      </div>
                      <h4>Drop files here or click to upload. (Max 3 files)</h4>
                    </div>
                  </div>
                )}
              </Dropzone>
            )}
            <ul className="list-unstyled mb-0" id="file-previews">
              {selectedFiles.map((file, index) => (
                <li className="mt-2 dz-image-preview" key={index}>
                  <div className="border rounded">
                    <div className="d-flex flex-wrap gap-2 p-2">
                      <div className="flex-shrink-0 me-3">
                        {index < 3 && (
                          <div className="avatar-sm bg-light rounded p-2">
                            <img
                              className="img-fluid rounded d-block"
                              src={file.url} // Use the url for the image source
                              alt={file.name}
                              style={{
                                objectFit: "cover",
                                width: "100%",
                                height: "100%",
                              }}
                            />
                          </div>
                        )}
                      </div>
                      <div className="flex-grow-1">
                        <div className="pt-1">
                          <h5 className="fs-md mb-1" data-dz-name>
                            {file.name} {/* Displaying the file name */}
                          </h5>
                          <strong
                            className="error text-danger"
                            data-dz-errormessage
                          ></strong>
                        </div>
                      </div>
                      <div className="flex-shrink-0 ms-3">
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={() => handleDelete(index)} // Use the handleDelete function
                        >
                          Delete
                        </Button>
                      </div>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </>
        )}
      </CardBody>
    </Card>
  );
};

export default ProductImageInput;
